import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Avatar, Box, Chip, Skeleton, Typography, useTheme } from '@mui/material'
import { IKPICard } from '../../../definitions/Shared'
import Formatter from '../../../helpers/Formatter'
import DataframeManager from '../../../managers/DataframeManager'
import { SparklineChart } from '../../charts'
import SmartTooltip from '../SmartTooltip'

interface P {
    label: string
    data: IKPICard
    isLoading?: boolean
    format?: 'compact' | 'number'
    noChart?: boolean
    unit?: string
    tooltipKey?: string
    chipLabel?: string
}

function KPICard({ data, label, unit, isLoading, noChart, tooltipKey, format, chipLabel }: P) {
    const theme = useTheme()

    const renderDelta = () => {
        if ((!data || !data.df) && !isLoading) return null

        const delta = data?.df?.delta
        return (
            <Chip
                icon={
                    isLoading
                        ? <Skeleton variant='circular' width={24} height={24} />
                        : (
                                <Avatar sx={{ height: 16, width: 16, bgcolor: 'default' }} variant='circular'>
                                    {delta > 0 ? <KeyboardArrowUp fontSize='inherit' /> : delta < 0 ? <KeyboardArrowDown fontSize='inherit' /> : ' - '}
                                </Avatar>
                            )
                }
                size='small'
                color='default'
                sx={{ fontWeight: 'bold' }}
                label={
                    isLoading
                        ? <Skeleton variant='text' sx={{ fontSize: 'body2.fontSize', width: 30, marginLeft: 0.5 }} />
                        : (delta > 0 ? '+' : '') + (format === 'number' ? Formatter.number(data?.value) : Formatter.compact(delta)) + (chipLabel ? ' ' + chipLabel : '')
                }
            />
        )
    }

    const prependUnit = () => (unit && unit === '$') ? unit : ''
    const appendUnit = () => (unit && unit !== '$') ? unit : ''

    const dataY = DataframeManager.getYs(data?.df)

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            <div style={{ marginBottom: theme.spacing(0.5) }}>
                <SmartTooltip tooltipKey={tooltipKey}>
                    <Typography variant='body2' fontWeight={400} style={{ textTransform: 'none', fontSize: 16, fontWeight: 600, textDecoration: tooltipKey ? 'underline dotted' : 'none' }}>
                        {label}
                    </Typography>
                </SmartTooltip>
            </div>
            <div style={{ display: 'flex', flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4, width: '100%' }}>
                <div style={{}}>
                    {isLoading
                        ? <Skeleton variant='text' sx={{ fontSize: 'h4.fontSize', width: 100 }} />
                        : (
                                <Typography variant='h5' fontWeight={500}>
                                    {prependUnit() + (format === 'number' ? Formatter.number(data?.value) : Formatter.compact(data?.value)) + appendUnit()}
                                </Typography>
                            )}
                </div>
                {(!noChart && dataY.length > 1) && (
                    <div style={{ flex: 1 }}>
                        <SparklineChart
                            isLoading={isLoading}
                            data={{
                                datasets: [{ data: dataY }],
                                labels: Array(dataY.length).fill(0).map((x, i) => i)
                            }}
                            options={{
                                maintainAspectRatio: false,
                                elements: {
                                    line: { borderWidth: 2, tension: 0.5 },
                                    point: { radius: 0 }
                                },
                                plugins: {
                                    legend: { display: false },
                                    tooltip: { enabled: false, callbacks: {} },
                                    datalabels: { display: false }
                                },
                                hover: { mode: null },
                                animation: { duration: 200 },
                                scales: {
                                    x: {
                                        display: false
                                    },
                                    y: {
                                        display: false
                                    }
                                }
                            }}
                            style={{ height: 50, paddingLeft: theme.spacing(3) }}
                        />
                    </div>
                )}
            </div>
            <Box marginTop={0.5} display='flex' flexDirection='row' justifyContent='space-between'>
                {renderDelta()}
            </Box>
        </div>
    )
}

export default KPICard
